import React, { useState, useEffect } from "react";
import axios from "axios";

const WalletTransactionsModal = ({
  show,
  onClose,
  wallet,
  transactions,
  onLoadMore,
  loading,
}) => {
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);

  // Fetch user data when wallet changes and owner type is User
  useEffect(() => {
    async function fetchUserData() {
      if (!wallet || wallet.ownerType !== "User") {
        setUserData(null);
        return;
      }

      setUserLoading(true);
      setUserError(null);
      try {
        const response = await axios.get(`/api/users/${wallet.owner}`);
        setUserData(response.data);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserError("Failed to load user information");
      } finally {
        setUserLoading(false);
      }
    }

    if (show && wallet) {
      fetchUserData();
    }
  }, [show, wallet]);

  // Filter transactions by type
  const filteredTransactions = transactions.filter((transaction) => {
    if (filterType === "all") return true;
    return transaction.transactionType === filterType;
  });

  // Calculate summary
  const summary = transactions.reduce(
    (acc, transaction) => {
      if (transaction.transactionType === "topup") {
        acc.totalTopUps += transaction.amount;
        acc.topUpCount++;
      } else {
        acc.totalDeductions += transaction.amount;
        acc.deductionCount++;
      }
      return acc;
    },
    {
      totalTopUps: 0,
      totalDeductions: 0,
      topUpCount: 0,
      deductionCount: 0,
    },
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Fixed formatDate function with proper error handling
  const formatDate = (dateString) => {
    // Handle null, undefined, or empty values
    if (!dateString) {
      return "N/A";
    }

    // Create date object
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "Invalid Date";
    }

    // Return formatted date
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTransactionIcon = (type) => {
    return type === "topup"
      ? "fa-plus-circle text-success"
      : "fa-minus-circle text-danger";
  };

  const getTransactionColor = (type) => {
    return type === "topup" ? "text-success" : "text-danger";
  };

  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    await onLoadMore(nextPage);
    setCurrentPage(nextPage);
  };

  if (!show || !wallet) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center py-3 px-4">
            <h5 className="modal-title mb-0 d-flex align-items-center">
              <i className="fa fa-list mr-3 text-primary"></i>
              <span className="font-weight-bold">Wallet Transactions</span>
            </h5>
            <button
              type="button"
              className="btn btn-light btn-sm rounded-circle p-2 ml-3"
              onClick={onClose}
              style={{
                width: "40px",
                height: "40px",
                border: "1px solid #dee2e6",
                fontSize: "18px",
                lineHeight: "1",
              }}
            >
              <span className="text-muted">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            {/* Wallet Info */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <h6>Wallet Owner</h6>
                    <p className="mb-0">
                      <span
                        className="font-weight-bold text-primary"
                        style={{
                          backgroundColor: "#e3f2fd",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      >
                        {wallet.ownerType}
                      </span>
                      <br />
                      {wallet.ownerType === "User" ? (
                        userLoading ? (
                          <small>
                            <i className="fa fa-spinner fa-spin"></i> Loading
                            user...
                          </small>
                        ) : userData ? (
                          <span>
                            {userData.name ||
                              (userData.firstName && userData.lastName
                                ? `${userData.firstName} ${userData.lastName}`
                                : wallet.owner)}
                            <br />
                            <small className="text-muted">{wallet.owner}</small>
                          </span>
                        ) : (
                          <span className="font-monospace text-sm">
                            {wallet.owner}
                          </span>
                        )
                      ) : (
                        <span className="font-monospace text-sm">
                          {wallet.owner}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <h6>Current Balance</h6>
                    <h4 className="text-success mb-0">
                      {formatCurrency(wallet.amount)}
                    </h4>
                  </div>
                  <div className="col-md-3">
                    <h6>Status</h6>
                    <span
                      className="font-weight-bold"
                      style={{
                        backgroundColor: wallet.isActive
                          ? "#d4edda"
                          : "#f8d7da",
                        color: wallet.isActive ? "#155724" : "#721c24",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      {wallet.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <h6>Total Transactions</h6>
                    <h4 className="text-primary mb-0">{transactions.length}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Summary */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="card border-success">
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h6 className="text-success">Total Top-ups</h6>
                        <h4 className="text-success mb-0">
                          {formatCurrency(summary.totalTopUps)}
                        </h4>
                        <small className="text-muted">
                          {summary.topUpCount} transactions
                        </small>
                      </div>
                      <div className="align-self-center">
                        <i className="fa fa-plus-circle fa-2x text-success"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-danger">
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h6 className="text-danger">Total Deductions</h6>
                        <h4 className="text-danger mb-0">
                          {formatCurrency(summary.totalDeductions)}
                        </h4>
                        <small className="text-muted">
                          {summary.deductionCount} transactions
                        </small>
                      </div>
                      <div className="align-self-center">
                        <i className="fa fa-minus-circle fa-2x text-danger"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Controls */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-0">
                      <label className="form-label">
                        Filter by Transaction Type
                      </label>
                      <select
                        className="form-control"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                      >
                        <option value="all">All Transactions</option>
                        <option value="topup">Top-ups Only</option>
                        <option value="deduction">Deductions Only</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-0">
                      <label className="form-label">Showing Results</label>
                      <p className="form-control-static">
                        {filteredTransactions.length} of {transactions.length}{" "}
                        transactions
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions List */}
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">Transaction History</h6>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading transactions...</p>
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No transactions found</p>
                    {filterType !== "all" && (
                      <p className="text-sm text-muted">
                        Try changing the filter to see more results
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Description</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((transaction, index) => (
                          <tr key={transaction._id || index}>
                            <td className="text-sm">
                              {formatDate(
                                transaction.createdAt || transaction.date,
                              )}
                            </td>
                            <td>
                              <span
                                className="font-weight-bold"
                                style={{
                                  backgroundColor:
                                    transaction.transactionType === "topup"
                                      ? "#d4edda"
                                      : "#f8d7da",
                                  color:
                                    transaction.transactionType === "topup"
                                      ? "#155724"
                                      : "#721c24",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                }}
                              >
                                <i
                                  className={`fa ${getTransactionIcon(transaction.transactionType)} mr-1`}
                                ></i>
                                {transaction.transactionType === "topup"
                                  ? "Top-up"
                                  : "Deduction"}
                              </span>
                            </td>
                            <td>
                              <strong
                                className={getTransactionColor(
                                  transaction.transactionType,
                                )}
                              >
                                {transaction.transactionType === "topup"
                                  ? "+"
                                  : "-"}
                                {formatCurrency(transaction.amount)}
                              </strong>
                            </td>
                            <td className="text-sm">
                              {transaction.description ||
                                transaction.purpose ||
                                "N/A"}
                            </td>
                            <td>
                              <span
                                className="font-weight-bold"
                                style={{
                                  backgroundColor:
                                    transaction.status === "completed" ||
                                    !transaction.status
                                      ? "#d4edda"
                                      : transaction.status === "pending"
                                        ? "#fff3cd"
                                        : "#f8d7da",
                                  color:
                                    transaction.status === "completed" ||
                                    !transaction.status
                                      ? "#155724"
                                      : transaction.status === "pending"
                                        ? "#856404"
                                        : "#721c24",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                }}
                              >
                                {transaction.status || "Completed"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Load More Button */}
            {!loading && filteredTransactions.length > 0 && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-chevron-down mr-1"></i>
                      Load More Transactions
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletTransactionsModal;
